import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/authStore';
import { DefaultAvatar } from '../../components/DefaultAvatar';
import { getPost, fetchComments, addComment, deletePost, deleteComment, incrementViewCount } from '../../services/boardService';
import type { BoardPost, BoardComment } from '../../types/board';
import { colors } from '../../styles/colors';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase/app';

const prefixColors: Record<string, string> = {
  question: colors.info,
  frequencyShare: colors.primary,
  review: colors.warning,
};

export default function BoardDetailPage() {
  const { postId } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const lang = i18n.language === 'ko' ? 'ko' : 'en';

  const [post, setPost] = useState<BoardPost | null>(null);
  const [comments, setComments] = useState<BoardComment[]>([]);
  const [commentText, setCommentText] = useState('');
  const [loading, setLoading] = useState(true);
  const [authorInfo, setAuthorInfo] = useState<Record<string, { gender?: string; profileImageUrl?: string }>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!postId) return;
    setLoading(true);
    Promise.all([
      getPost(lang, postId),
      fetchComments(lang, postId),
    ]).then(([p, c]) => {
      setPost(p);
      setComments(c);
      // Increment view count once per session per post
      const viewKey = `viewed_${postId}`;
      if (!sessionStorage.getItem(viewKey)) {
        sessionStorage.setItem(viewKey, '1');
        incrementViewCount(lang, postId).catch(() => {});
      }
    }).finally(() => setLoading(false));
  }, [postId, lang]);

  // Fetch author info for post + comments without authorGender
  useEffect(() => {
    if (!post) return;
    const allAuthors = [post, ...comments];
    const uids = [...new Set(allAuthors.filter(a => !(a as any).authorGender && a.authorId).map(a => a.authorId))];
    if (uids.length === 0) return;
    Promise.all(uids.map(async uid => {
      try {
        const snap = await getDoc(doc(db, 'users', uid));
        if (snap.exists()) {
          const d = snap.data();
          return [uid, { gender: d.gender, profileImageUrl: d.profileImageUrl }] as const;
        }
      } catch {}
      return null;
    })).then(results => {
      const info: Record<string, { gender?: string; profileImageUrl?: string }> = {};
      for (const r of results) if (r) info[r[0]] = r[1];
      setAuthorInfo(prev => ({ ...prev, ...info }));
    });
  }, [post, comments]);

  const handleAddComment = async () => {
    if (!commentText.trim() || !user || !postId || submitting) return;
    // Content filter check
    const { isContentClean, getFilterMessage } = await import('../../services/contentFilter');
    if (!isContentClean(commentText.trim())) { alert(getFilterMessage(i18n.language)); return; }
    setSubmitting(true);
    try {
      await addComment(lang, postId, {
        content: commentText.trim(),
        authorId: user.uid,
        authorNickname: user.nickname,
      });
      setCommentText('');
      // Reload comments
      const c = await fetchComments(lang, postId);
      setComments(c);
      if (post) setPost({ ...post, commentCount: (post.commentCount ?? 0) + 1 });
    } catch (err) {
      console.error('[Board] Comment failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePost = async () => {
    if (!postId || !confirm(t('common.delete') + '?')) return;
    await deletePost(lang, postId);
    navigate('/board');
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!postId || !confirm(t('common.delete') + '?')) return;
    await deleteComment(lang, postId, commentId);
    setComments((prev) => prev.filter((c) => c.id !== commentId));
    if (post) setPost({ ...post, commentCount: Math.max(0, (post.commentCount ?? 1) - 1) });
  };

  const formatDateTime = (ts: any) => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return `${d.getFullYear()}.${(d.getMonth() + 1).toString().padStart(2, '0')}.${d.getDate().toString().padStart(2, '0')} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center" style={{ backgroundColor: colors.background }}>
        <p className="text-sm" style={{ color: colors.textMuted }}>{t('common.loading')}</p>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="flex h-full flex-col items-center justify-center" style={{ backgroundColor: colors.background }}>
        <p className="text-sm" style={{ color: colors.textMuted }}>{t('board.noPost')}</p>
        <Link to="/board" className="mt-3 text-sm" style={{ color: colors.primary }}>← {t('common.back')}</Link>
      </div>
    );
  }

  const isAuthor = user?.uid === post.authorId;

  return (
    <div className="flex h-full flex-col" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3"
        style={{ borderBottom: `1px solid ${colors.border}` }}>
        <Link to="/board" className="text-sm" style={{ color: colors.secondary }}>
          ← {t('common.back')}
        </Link>
        {isAuthor && (
          <div className="flex gap-2">
            <Link to={`/board/write?edit=${postId}`}
              className="text-xs font-semibold" style={{ color: colors.primary }}>
              {t('common.edit')}
            </Link>
            <button onClick={handleDeletePost}
              className="text-xs font-semibold" style={{ color: colors.error }}>
              {t('common.delete')}
            </button>
          </div>
        )}
      </div>

      {/* Post Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4">
          {/* Title row */}
          <div className="mb-2 flex items-center gap-2">
            {post.isNotice && (
              <span className="rounded px-1.5 py-0.5 text-[10px] font-bold text-white"
                style={{ backgroundColor: colors.error }}>
                {t('board.notice')}
              </span>
            )}
            {post.prefix && post.prefix !== 'none' && (
              <span className="rounded px-1.5 py-0.5 text-[10px] text-white"
                style={{ backgroundColor: prefixColors[post.prefix] || colors.textMuted }}>
                {t(`board.${post.prefix}`)}
              </span>
            )}
          </div>
          <h2 className="mb-2 text-lg font-bold" style={{ color: colors.text }}>{post.title}</h2>

          {/* Author info */}
          <div className="mb-4 flex items-center gap-2 text-[11px]" style={{ color: colors.textMuted }}>
            <button
              onClick={(e) => {
                window.dispatchEvent(new CustomEvent('open-user-menu', {
                  detail: { uid: post.authorId, nickname: post.authorNickname || (post as any).author || '?', x: e.clientX, y: e.clientY },
                }));
              }}
              className="flex items-center gap-1.5 font-semibold hover:underline"
              style={{ color: colors.textSecondary }}
            >
              <div className="flex h-6 w-6 items-center justify-center overflow-hidden rounded-full" style={{ backgroundColor: '#222', border: `1.5px solid ${colors.border}` }}>
                {((post as any).authorProfileImageUrl || authorInfo[post.authorId]?.profileImageUrl)
                  ? <img src={(post as any).authorProfileImageUrl || authorInfo[post.authorId]?.profileImageUrl} alt="" className="h-full w-full object-cover" />
                  : <DefaultAvatar gender={(post as any).authorGender ?? authorInfo[post.authorId]?.gender ?? 'unknown'} size={14} />}
              </div>
              {post.authorNickname || (post as any).author || '?'}
            </button>
            <span>{formatDateTime(post.createdAt)}</span>
          </div>

          {/* Content */}
          <div className="mb-6 rounded-lg p-4 text-sm leading-relaxed whitespace-pre-wrap"
            style={{ backgroundColor: colors.card, color: colors.text }}>
            {post.content.split(/(https?:\/\/[^\s]+)/g).map((part, i) =>
              /^https?:\/\//.test(part)
                ? <a key={i} href={part} target="_blank" rel="noopener" className="underline" style={{ color: colors.primary }}>{part}</a>
                : part
            )}
          </div>

          {/* Comments Section */}
          <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: 16 }}>
            <h3 className="mb-3 text-sm font-bold" style={{ color: colors.text }}>
              {t('board.comments')} ({comments.length})
            </h3>

            {comments.length === 0 ? (
              <p className="mb-4 text-xs" style={{ color: colors.textMuted }}>
                {i18n.language === 'ko' ? '첫 댓글을 남겨보세요!' : 'Be the first to comment!'}
              </p>
            ) : (
              <div className="mb-4 flex flex-col gap-2">
                {comments.filter((c) => !user?.blockedUserIds?.includes(c.authorId)).map((c) => (
                  <div key={c.id} className="rounded-lg px-3 py-2.5" style={{ backgroundColor: colors.card }}>
                    <div className="mb-1 flex items-center justify-between">
                      <button
                        onClick={(e) => {
                          window.dispatchEvent(new CustomEvent('open-user-menu', {
                            detail: { uid: c.authorId, nickname: c.authorNickname || (c as any).author || '?', x: e.clientX, y: e.clientY },
                          }));
                        }}
                        className="flex items-center gap-1 text-xs font-bold hover:underline"
                        style={{ color: colors.textSecondary }}
                      >
                        <div className="flex h-5 w-5 items-center justify-center overflow-hidden rounded-full" style={{ backgroundColor: '#222', border: `1px solid ${colors.border}` }}>
                          {((c as any).authorProfileImageUrl || authorInfo[c.authorId]?.profileImageUrl)
                            ? <img src={(c as any).authorProfileImageUrl || authorInfo[c.authorId]?.profileImageUrl} alt="" className="h-full w-full object-cover" />
                            : <DefaultAvatar gender={(c as any).authorGender ?? authorInfo[c.authorId]?.gender ?? 'unknown'} size={12} />}
                        </div>
                        {c.authorNickname}
                      </button>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px]" style={{ color: colors.textMuted }}>
                          {formatDateTime(c.createdAt)}
                        </span>
                        {user?.uid === c.authorId && (
                          <button
                            onClick={() => handleDeleteComment(c.id)}
                            className="text-[10px] font-semibold" style={{ color: colors.error }}>
                            {t('common.delete')}
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="text-sm" style={{ color: colors.text }}>{c.content}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Comment input */}
            <div className="flex gap-2">
              <input
                type="text"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder={t('board.commentPlaceholder')}
                className="flex-1 rounded-lg px-3 py-2 text-sm outline-none"
                style={{
                  backgroundColor: colors.card,
                  color: colors.text,
                  border: `1px solid ${colors.border}`,
                }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddComment(); }}
              />
              <button
                onClick={handleAddComment}
                disabled={!commentText.trim() || submitting}
                className="rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                style={{ backgroundColor: colors.primary }}
              >
                {t('common.send')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
